import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Category } from '~/components/Navigation/BlogNavigation'
import { ArticleCardProps } from '~/components/Slices/ArticleCard'
import useArticlesList from '~/components/Slices/BlogGrid/hooks/useArticlesList'

import { queryParamToArrayParams } from '~/data/blog-page-data/serializer'

interface UseLoadMoreArticlesListArgs {
  articles: ArticleCardProps[]
  categories: Category[]
  maxPage: number
  previewRef?: string
}

export default function useLoadMoreArticlesList(
  args: UseLoadMoreArticlesListArgs,
) {
  const { query } = useRouter()
  const [articles, setArticles] = useState(args.articles)
  const [reset, setReset] = useState(false)

  const {
    articles: data,
    maxPage,
    isLoading,
    isFetching,
    page,
    setPage,
    setCategoryIds,
  } = useArticlesList(args?.previewRef)

  const categoryIds = useMemo(
    () =>
      queryParamToArrayParams(query?.categories)?.map(
        (key) => args.categories?.find((item) => item?.key === key)?.id,
      ),
    [query?.categories, args.categories],
  )

  const firstUpdate = useRef(true)
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    setCategoryIds(categoryIds)
    setPage(1)
    setReset(true)
  }, [categoryIds])

  const loadMore = useCallback(() => {
    setCategoryIds(categoryIds)
    setPage((page) => page + 1)
  }, [categoryIds])

  const hasLoadMore = useMemo(
    () => (maxPage || args.maxPage) < page,
    [args.maxPage, maxPage, page],
  )

  const count = useMemo(() => articles?.length ?? 0, [articles])

  useEffect(() => {
    data && setArticles((prev) => (reset ? data : [...prev, ...data]))
    reset && setReset(false)
  }, [data])

  return {
    articles,
    count,
    loadMore,
    hasLoadMore,
    isLoading,
    isFetching,
  }
}
